import type { ProductionDowntime } from "./models";
import { z } from "zod";

export type DowntimeReasonSubmission = { note: string } & (
  { reasonCodeId: string; customReason?: never }
  | { customReason: string; reasonCodeId?: never }
);
export type DowntimeReasonRequest = DowntimeReasonSubmission & { idempotencyKey?: string };

const nullableText = z.string().nullable();
const productionDowntimeResponseSchema = z.object({
  id: z.string(),
  reasonCodeId: nullableText,
  reasonName: nullableText,
  planned: z.boolean(),
  note: nullableText,
  startedAt: z.string(),
  endedAt: nullableText,
  startedBy: nullableText,
  startedByName: z.string(),
  startedSessionId: nullableText,
  terminalId: nullableText,
  endedBy: nullableText,
  endedByName: nullableText,
  endedSessionId: nullableText,
  reasonAssignedBy: nullableText,
  reasonAssignedByName: nullableText,
  reasonAssignedSessionId: nullableText,
  reasonAssignedAt: nullableText,
  correctionSupervisorApprovalId: nullableText,
  correctedBy: nullableText,
  correctedByName: nullableText,
  correctedSessionId: nullableText,
  correctedAt: nullableText,
  correctionPreviousValues: z.object({ reasonCodeId: nullableText, reasonName: nullableText.optional(), note: nullableText }).nullable(),
});

/** Validates a durable downtime mutation response before it can update the tablet ledger. */
export function parseProductionDowntimeResponse(value: unknown): ProductionDowntime {
  return productionDowntimeResponseSchema.parse(value);
}

export function downtimeReasonSubmission(draft: { reasonCodeId: string; customReason: string; note: string }): DowntimeReasonSubmission | null {
  const reasonCodeId = draft.reasonCodeId.trim();
  const customReason = draft.customReason.trim();
  const note = draft.note.trim();
  if (reasonCodeId && !customReason) return { reasonCodeId, note };
  if (!reasonCodeId && customReason.length >= 3) return { customReason, note };
  return null;
}

/** Custom text is durable only with the draft's stable intent identifier. */
export function downtimeReasonRequest(draft: { reasonCodeId: string; customReason: string; note: string }, intentId: string): DowntimeReasonRequest | null {
  const submission = downtimeReasonSubmission(draft);
  if (!submission) return null;
  return "customReason" in submission ? { ...submission, idempotencyKey: intentId } : submission;
}
